// src/components/AssociationPendingMembers.js
import React, { useEffect, useMemo, useState } from "react";
import { Search, MoreHorizontal, X, Download } from "lucide-react";

const API_BASE_URL = "https://aaapd.org.et/gallery_backend/api";
const ATTACH_BASE_URL = API_BASE_URL.replace(/\/api$/, "") + "/self_registered_attachments";
const subcities = [
  'አዲስ ከተማ',
  'አቃቂ ቃሊቲ',
  'አራዳ',
  'ቦሌ',
  'ጉለሌ',
  'ቂርቆስ',
  'ኮልፌ ቀራኒዮ',
  'ለሚ ኩራ',
  'ልደታ',
  'ንፋስ ስልክ ላፍቶ',
  'የካ',
];

export default function AssociationPendingMembers() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDetails, setShowDetails] = useState(false);
  const [detailMember, setDetailMember] = useState(null);
  const [attachmentPreview, setAttachmentPreview] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [opds, setOpds] = useState([]);
  const [selectedSubCity, setSelectedSubCity] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectMember, setRejectMember] = useState(null);

  const isImageFile = (filename) => {
    if (!filename) return false;
    const ext = filename.split(".").pop().toLowerCase();
    return ["jpg", "jpeg", "png", "gif", "bmp", "webp", "svg"].includes(ext);
  };

  useEffect(() => {
    fetchMembers();
    fetchOpds();
  }, []);

  // reset page when filters/search change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedSubCity]);

  const fetchMembers = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/getAllMembers.php`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      const list = Array.isArray(data) ? data : [];
      setMembers(list.filter((m) => (m.aaapd_status || "").toString().toLowerCase() === "pending"));
    } catch (e) {
      console.error("Failed to fetch members:", e);
      setMembers([]);
    }
    setLoading(false);
  };

  const fetchOpds = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/get_opds.php`);
      const data = await res.json();
      if (data?.status === 'success' && Array.isArray(data.data)) {
        setOpds(data.data);
      } else if (Array.isArray(data)) {
        setOpds(data);
      }
    } catch (e) {
      setOpds([]);
    }
  };

  const emailToOpdName = useMemo(() => {
    const map = {};
    opds.forEach(o => {
      if (o?.contact_email && o?.name) map[o.contact_email] = o.name;
    });
    return map;
  }, [opds]);

  const opdNames = useMemo(() => {
    const names = opds.map(o => o?.name).filter(Boolean);
    return Array.from(new Set(names)).sort((a, b) => a.localeCompare(b));
  }, [opds]);

  const filteredMembers = useMemo(() => {
    return members.filter((member) => {
      const fullName = `${member.firstName} ${member.middleName || ""} ${member.lastName}`.toLowerCase();
      if (
        searchTerm &&
        !fullName.includes(searchTerm.toLowerCase()) &&
        !((member.idNumber || member.uniqueID || "").toString().toLowerCase().includes(searchTerm.toLowerCase()))
      ) {
        return false;
      }
      if (selectedSubCity !== 'all') {
        const memberSubCity = (member.subCity || '').toString();
        if (memberSubCity !== selectedSubCity) return false;
      }
      return true;
    });
  }, [members, searchTerm, selectedSubCity, emailToOpdName]);

  const paginatedMembers = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredMembers.slice(start, start + itemsPerPage);
  }, [filteredMembers, currentPage]);

  const totalPages = Math.ceil(filteredMembers.length / itemsPerPage) || 1;

  const handleDetail = (member) => {
    setDetailMember(member);
    setShowDetails(true);
  };
  const closeDetailModal = () => {
    setShowDetails(false);
    setDetailMember(null);
  };

  const handleStatusChange = async (memberId, newStatus) => {
    if (newStatus === 'rejected') {
      const member = members.find((m) => m.id === memberId);
      setRejectMember(member || null);
      setRejectReason("");
      setShowRejectModal(true);
      return;
    }

    try {
      const member = members.find((m) => m.id === memberId);
      const requestEmail = member?.opdEmail || JSON.parse(localStorage.getItem('currentUser'))?.email || '';
      const res = await fetch(`${API_BASE_URL}/updateMember.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: memberId, aaapd_status: newStatus, requestEmail })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.status === 'error') {
        throw new Error(data.message || `Failed to update status to ${newStatus}`);
      }
      // Refresh or remove from pending list after status change
      setMembers((prev) => prev.filter((m) => m.id !== memberId));
      // Close details modal if the same member is open
      if (detailMember && detailMember.id === memberId) {
        closeDetailModal();
      }
      alert(`Member ${newStatus}.`);
    } catch (e) {
      console.error(e);
      alert(e.message || 'Failed to update member status');
    }
  };

  const confirmReject = async (e) => {
    e.preventDefault();
    if (!rejectMember) {
      setShowRejectModal(false);
      setRejectReason("");
      return;
    }

    try {
      const memberId = rejectMember.id;
      const requestEmail = rejectMember?.opdEmail || JSON.parse(localStorage.getItem('currentUser'))?.email || '';
      const payload = { id: memberId, aaapd_status: 'rejected', requestEmail };
      // send reason to backend using the rejection_reason column
      if (rejectReason && rejectReason.trim()) {
        payload.rejection_reason = rejectReason.trim();
      }

      const res = await fetch(`${API_BASE_URL}/updateMember.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.status === 'error') {
        throw new Error(data.message || 'Failed to reject member');
      }

      setMembers((prev) => prev.filter((m) => m.id !== memberId));
      if (detailMember && detailMember.id === memberId) {
        closeDetailModal();
      }

      setShowRejectModal(false);
      setRejectReason("");
      setRejectMember(null);
      alert('Member rejected.');
    } catch (err) {
      console.error(err);
      alert(err.message || 'Failed to reject member');
    }
  };

  const exportToCSV = () => {
    if (filteredMembers.length === 0) {
      alert("No members to export");
      return;
    }
    // Comprehensive headers aligned with other pages
    const headers = [
      "Full Name",
      "Gender",
      "Date of Birth",
      "Age",
      "Phone Number",
      "Alternative Phone Number",
      "Email",
      "Location",
      "Specific Location",
      "Sub City",
      "Woreda",
      "House Number",
      "Disability Type",
      "Disability Other",
      "Disability Description",
      "Education Level",
      "Marital Status",
      "Number of Families",
      "Employment Status",
      "Support Needed",
      "Work Needed",
      "Support Received",
      "Place of Work",
      "Office Name",
      "Type of the Work",
      "Job Responsibility",
      "Labour ID",
      "Labour ID Number",
      "Member Status",
      "ID Type",
      "ID Number",
      "Unique ID",
      "Passport Photo URL",
      "Receipt/ID Attachment URL",
    ];

    const escapeCsv = (v) => `"${(v ?? '').toString().replace(/"/g, '""')}"`;
    const rows = filteredMembers.map((m) => {
      const fullName = `${m.firstName || ''} ${m.middleName ? m.middleName + ' ' : ''}${m.lastName || ''}`.trim();
      const photoUrl = m.photoAttachment ? `${ATTACH_BASE_URL}/${m.photoAttachment}` : '';
      const idAttachUrl = m.idAttachment ? `${ATTACH_BASE_URL}/${m.idAttachment}` : '';
      const disabilityType = m.disabilityType === 'Other' ? (m.disabilityOther || 'Other') : (m.disabilityType || '');
      return [
        fullName,
        m.gender || '',
        m.dateOfBirth || '',
        m.age || '',
        m.phoneNumber || '',
        m.AltPhoneNumber || '',
        m.email || '',
        m.location || '',
        m.specificLocation || '',
        m.subCity || '',
        m.woreda || '',
        m.houseNumber || '',
        disabilityType,
        m.disabilityOther || '',
        m.disabilityDescription || '',
        m.educationLevel || '',
        m.maritalStatus || '',
        m.numberOfFamilies || '',
        m.employmentStatus || '',
        m.supportNeeded || '',
        m.workNeeded || '',
        m.supportReceived || '',
        m.workPlace || '',
        m.officeName || '',
        m.workType || '',
        m.jobResponsibility || '',
        m.labourID || '',
        m.labourIDNumber || '',
        m.aaapd_status || '',
        m.idType || '',
        m.idNumber || '',
        m.uniqueID || '',
        photoUrl,
        idAttachUrl,
      ];
    });

    const csv = [headers, ...rows]
      .map((r) => r.map(escapeCsv).join(","))
      .join("\n");

    // Prepend BOM for Amharic text compatibility in Excel
    const csvWithBom = "\uFEFF" + csv;
    const blob = new Blob([csvWithBom], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `pending_members_${new Date().toISOString().replace(/[:.]/g, "-")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="bg-gray-100 min-h-screen p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        <header className="mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Pending Members</h1>
          <p className="text-sm text-gray-500 mt-1">Members with status Pending awaiting review.</p>
        </header>

        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search members (name or ID)"
                className="pl-10 pr-4 py-2 w-full border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                aria-label="Search members"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  aria-label="Clear search"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

        {showRejectModal && rejectMember && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-2">Reject Member</h2>
              <p className="text-sm text-gray-600 mb-4">
                Please provide a reason for rejecting
                {" "}
                <span className="font-semibold">
                  {rejectMember.firstName} {rejectMember.middleName ? rejectMember.middleName + " " : ""}{rejectMember.lastName}
                </span>
                .
              </p>
              <form onSubmit={confirmReject} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
                  <textarea
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    rows={4}
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    required
                    placeholder="Enter reason for rejection"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowRejectModal(false);
                      setRejectReason("");
                      setRejectMember(null);
                    }}
                    className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700"
                  >
                    Confirm Reject
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
            <select
              value={selectedSubCity}
              onChange={(e) => setSelectedSubCity(e.target.value)}
              className="w-56 border rounded-lg px-3 py-2 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              aria-label="Filter by Sub City"
              title="Filter by Sub City"
            >
              <option value="all">ሁሉም ክፍለ ከተሞች</option>
              {subcities.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
            <button
              onClick={exportToCSV}
              disabled={filteredMembers.length === 0}
              className="flex items-center space-x-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Export to CSV"
            >
              <Download className="h-4 w-4" />
              <span>Export CSV</span>
            </button>
          </div>
        </div>

        {/* Attachment Lightbox Modal */}
        {attachmentPreview && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80">
            <div className="relative max-w-5xl max-h-[90vh] w-auto m-4">
              <button
                type="button"
                className="absolute -top-3 -right-3 bg-white text-gray-700 rounded-full shadow p-2 hover:bg-gray-100"
                onClick={() => setAttachmentPreview(null)}
                aria-label="Close attachment preview"
              >
                <X size={20} />
              </button>
              <img
                src={attachmentPreview}
                alt="Attachment Preview"
                className="max-h-[85vh] max-w-full rounded-lg shadow-2xl"
              />
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {loading ? (
            <div className="text-center py-12">
              <p className="text-gray-500">Loading pending members...</p>
            </div>
          ) : filteredMembers.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">No pending members found.</p>
            </div>
          ) : (
            <>
              {/* Table for md+ */}
              <div className="hidden md:block overflow-x-auto">
                <table className="min-w-full bg-white">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="py-3 px-4 text-left font-semibold text-gray-600 text-sm uppercase tracking-wider w-12">#</th>
                      <th className="py-3 px-6 text-left font-semibold text-gray-600 text-sm uppercase tracking-wider">Full Name</th>
                      <th className="py-3 px-6 text-left font-semibold text-gray-600 text-sm uppercase tracking-wider">Gender</th>
                      <th className="py-3 px-6 text-left font-semibold text-gray-600 text-sm uppercase tracking-wider">ID Number</th>
                      <th className="py-3 px-6 text-left font-semibold text-gray-600 text-sm uppercase tracking-wider">Membership Status</th>
                      <th className="py-3 px-6 text-center font-semibold text-gray-600 text-sm uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {paginatedMembers.map((member, index) => (
                      <tr key={member.id} className="hover:bg-gray-50 transition">
                        <td className="py-4 px-4 whitespace-nowrap text-gray-600">{(currentPage - 1) * itemsPerPage + index + 1}</td>
                        <td className="py-4 px-6 whitespace-nowrap">{`${member.firstName} ${member.middleName ? member.middleName + " " : ""}${member.lastName}`}</td>
                        <td className="py-4 px-6 whitespace-nowrap">{member.gender}</td>
                        <td className="py-4 px-6 whitespace-nowrap">{member.uniqueID || '-'}</td>
                        <td className="py-4 px-6 whitespace-nowrap">
                          {(() => {
                            const v = (member.aaapd_status || '').toString();
                            const s = v.toLowerCase();
                            const cls = s === 'approved'
                              ? 'bg-green-100 text-green-800'
                              : s === 'rejected'
                              ? 'bg-red-100 text-red-800'
                              : s === 'pending'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-gray-100 text-gray-800';
                            return (
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${cls}`}>
                                {v || '-'}
                              </span>
                            );
                          })()}
                        </td>
                        <td className="py-4 px-6 whitespace-nowrap text-center">
                          <div className="flex items-center justify-center gap-3">
                            <button
                              onClick={() => handleDetail(member)}
                              className="text-teal-600 hover:text-teal-700 font-medium"
                              title="View Details"
                            >
                              Details
                            </button>
                            <button
                              onClick={() => handleStatusChange(member.id, 'approved')}
                              className="px-3 py-1 rounded bg-green-100 text-green-700 hover:bg-green-200 text-sm font-medium"
                              title="Approve Member"
                            >
                              Approve
                            </button>
                            <button
                              onClick={() => handleStatusChange(member.id, 'rejected')}
                              className="px-3 py-1 rounded bg-red-100 text-red-700 hover:bg-red-200 text-sm font-medium"
                              title="Reject Member"
                            >
                              Reject
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Cards for small screens */}
              <div className="md:hidden grid grid-cols-1 sm:grid-cols-2 gap-4 p-4">
                {paginatedMembers.map((member) => (
                  <div key={member.id} className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 flex flex-col justify-between">
                    <div>
                      <h4 className="font-bold text-lg text-gray-800">{`${member.firstName} ${member.lastName}`}</h4>
                      <p className="text-sm text-gray-600">ID: {member.uniqueID || '-'}</p>
                      <p className="text-sm text-gray-600">{member.gender}</p>
                    </div>
                    <div className="flex items-center justify-end gap-3 mt-4">
                      <button
                        onClick={() => handleDetail(member)}
                        className="text-teal-600 hover:text-teal-700 font-medium"
                        title="View Details"
                      >
                        Details
                      </button>
                      <button
                        onClick={() => handleStatusChange(member.id, 'approved')}
                        className="px-3 py-1 rounded bg-green-100 text-green-700 hover:bg-green-200 text-sm font-medium"
                        title="Approve Member"
                      >
                        Approve
                      </button>
                      <button
                        onClick={() => handleStatusChange(member.id, 'rejected')}
                        className="px-3 py-1 rounded bg-red-100 text-red-700 hover:bg-red-200 text-sm font-medium"
                        title="Reject Member"
                      >
                        Reject
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination (shown once for both table and card views) */}
              <div className="flex flex-col md:flex-row justify-between items-center p-4 gap-4 border-t border-gray-100">
                <span className="text-sm text-gray-600">
                  Showing {paginatedMembers.length} of {filteredMembers.length} members
                </span>
                <div className="flex items-center gap-2">
                  <button
                    className="px-3 py-1 border rounded-lg bg-white text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition"
                    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                  >
                    Prev
                  </button>
                  <span className="text-sm text-gray-700">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    className="px-3 py-1 border rounded-lg bg-white text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition"
                    onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Details Modal (parity with AssociationMembers) */}
        {showDetails && detailMember && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 transition-opacity duration-300 ease-in-out" aria-modal="true" role="dialog">
            <div className="relative bg-gray-100 rounded-xl shadow-2xl w-full max-w-3xl m-4 max-h-[90vh] flex flex-col animate-fade-in-down">
              <div className="flex items-center justify-between p-5 border-b border-gray-200 rounded-t-xl bg-white">
                <h3 id="member-details-title" className="text-xl font-semibold text-gray-800">
                  Member Details
                </h3>
                <div className="flex items-center gap-3">
                  {detailMember.photoAttachment && (
                    isImageFile(detailMember.photoAttachment) ? (
                      <button
                        type="button"
                        className="group"
                        title="View passport photo"
                        onClick={() => setAttachmentPreview(`${ATTACH_BASE_URL}/${detailMember.photoAttachment}`)}
                      >
                        <img
                          src={`${ATTACH_BASE_URL}/${detailMember.photoAttachment}`}
                          alt="Passport Photo"
                          className="h-24 w-20 rounded object-cover object-top border border-gray-200 shadow-sm group-hover:opacity-90"
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                      </button>
                    ) : (
                      <a
                        href={`${ATTACH_BASE_URL}/${detailMember.photoAttachment}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-teal-600 hover:underline"
                        title="View passport photo"
                      >
                        Passport Photo
                      </a>
                    )
                  )}
                  <button
                    type="button"
                    className="p-2 text-gray-400 bg-transparent rounded-lg hover:bg-gray-200 hover:text-gray-900"
                    onClick={() => { setShowDetails(false); setDetailMember(null); }}
                    aria-label="Close details"
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>
              <div className="p-6 flex-grow overflow-y-auto">
                <div className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {[
                      { label: "Full Name", value: `${detailMember.firstName} ${detailMember.middleName || ""} ${detailMember.lastName}` },
                      { label: "Gender", value: detailMember.gender },
                      { label: "Date of Birth", value: detailMember.dateOfBirth },
                      { label: "Age", value: detailMember.age },
                      { label: "Phone Number", value: detailMember.phoneNumber },
                      { label: "Alternative Phone Number", value: detailMember.AltPhoneNumber },
                      { label: "Email", value: detailMember.email },
                      { label: "Location", value: detailMember.location },
                      { label: "Sub City", value: detailMember.subCity },
                      { label: "Woreda", value: detailMember.woreda },
                      { label: "House Number", value: detailMember.houseNumber },
                      { label: "Specific Location", value: detailMember.specificLocation },
                      { label: "Disability Type", value: detailMember.disabilityType === "Other" ? detailMember.disabilityOther || "Other" : detailMember.disabilityType },
                      { label: "Disability Description", value: detailMember.disabilityDescription },
                      { label: "Education Level", value: detailMember.educationLevel },
                      { label: "Marital Status", value: detailMember.maritalStatus },
                      { label: "Number of Families", value: detailMember.numberOfFamilies },
                      { label: "Employment Status", value: detailMember.employmentStatus },
                      { label: "Member Status", value: detailMember.aaapd_status },
                      { label: "Labour ID", value: detailMember.labourID },
                      { label: "Labour ID Number", value: detailMember.labourIDNumber },
                      { label: "Place of Work", value: detailMember.workPlace },
                      { label: "Name of the Office", value: detailMember.officeName },
                      { label: "Type of the Work", value: detailMember.workType },
                      { label: "Job Responsibilities", value: detailMember.jobResponsibility },
                      { label: "Support Needed", value: detailMember.supportNeeded },
                      { label: "Work Needed", value: detailMember.workNeeded },
                      { label: "Support Received", value: detailMember.supportReceived },
                      { label: "ID Type", value: detailMember.idType },
                      { label: "ID Number", value: detailMember.idNumber },
                    ].map(({ label, value }) => (
                      <div key={label}>
                        <p className="text-sm font-medium text-gray-500">{label}</p>
                        {label === "Member Status" ? (
                          (() => {
                            const v = (value || "").toString();
                            const s = v.toLowerCase();
                            const cls = s === "approved"
                              ? "bg-green-100 text-green-800"
                              : s === "rejected"
                              ? "bg-red-100 text-red-800"
                              : s === "pending"
                              ? "bg-yellow-100 text-yellow-800"
                              : "bg-gray-100 text-gray-800";
                            return (
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${cls}`}>
                                {v || "-"}
                              </span>
                            );
                          })()
                        ) : (
                          <p className="text-base text-gray-800 font-semibold">{value || "-"}</p>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* ID/Receipt Attachment Preview */}
                  {detailMember.idAttachment && (
                    <div className="mt-4">
                      <p className="text-sm font-medium text-gray-500 mb-2">Receipt Attachment</p>
                      {isImageFile(detailMember.idAttachment) ? (
                        <button
                          type="button"
                          className="group"
                          onClick={() => setAttachmentPreview(`${ATTACH_BASE_URL}/${detailMember.idAttachment}`)}
                          title="Click to enlarge"
                        >
                          <img
                            src={`${ATTACH_BASE_URL}/${detailMember.idAttachment}`}
                            alt="Receipt Attachment"
                            className="max-h-64 rounded-lg border border-gray-200 shadow-sm group-hover:opacity-90"
                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                          />
                        </button>
                      ) : (
                        <a
                          href={`${ATTACH_BASE_URL}/${detailMember.idAttachment}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-teal-600 hover:underline"
                        >
                          View/Download Attachment
                        </a>
                      )}
                    </div>
                  )}

                  {/* Passport Photo Preview */}
                  {detailMember.photoAttachment && (
                    <div className="mt-4">
                      <p className="text-sm font-medium text-gray-500 mb-2">Passport Photo</p>
                      {isImageFile(detailMember.photoAttachment) ? (
                        <button
                          type="button"
                          className="group"
                          onClick={() => setAttachmentPreview(`${ATTACH_BASE_URL}/${detailMember.photoAttachment}`)}
                          title="Click to enlarge"
                        >
                          <img
                            src={`${ATTACH_BASE_URL}/${detailMember.photoAttachment}`}
                            alt="Passport Photo"
                            className="max-h-64 rounded-lg border border-gray-200 shadow-sm group-hover:opacity-90"
                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                          />
                        </button>
                      ) : (
                        <a
                          href={`${ATTACH_BASE_URL}/${detailMember.photoAttachment}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-teal-600 hover:underline"
                        >
                          View/Download Photo
                        </a>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center justify-end p-4 border-t border-gray-200 bg-white rounded-b-xl">
                <button
                  type="button"
                  onClick={closeDetailModal}
                  className="px-6 py-2 rounded-lg bg-gray-200 text-gray-800 hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400 transition"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
